import React, { useState } from 'react';
import {
  Card,
  Button,
  Switch,
  Alert,
  Space,
  Typography,
  Divider,
  message,
  Row,
  Col,
  Spin
} from 'antd';
import {
  ImportOutlined,
  InfoCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import apiClient from '../../services/apiClient';

const { Title, Text, Paragraph } = Typography;

const ImportEsimInventory: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [lastImportResult, setLastImportResult] = useState<any>(null);

  const handleImport = async () => {
    try {
      setLoading(true);
      
      const response = await apiClient.post('/v1/packs/transatel-api', {
        msisdn : '0',
        childOf : '0',
        tags :'0',
        acceptLanguage :'0'
      });

      setLastImportResult({
        success: true,
        data: response.data,
        timestamp: new Date()
      });

      message.success('Importation d\'inventaire eSIM réussie!');
    } catch (error: any) {
      console.error('Erreur lors de l\'importation:', error);
      
      setLastImportResult({
        success: false,
        error: error.response?.data?.message || error.message,
        timestamp: new Date()
      });

      message.error('Erreur lors de l\'importation d\'inventaire eSIM');
    } finally {
      setLoading(false);
    }
  };
const handleSync = async () => {
    try {
      setLoading(true);
      
      const response = await apiClient.get('/admin/import-esim-inventory', {
        params: {
          forceUpdate
        }
      });

      setLastImportResult({
        success: true,
        data: response.data,
        timestamp: new Date()
      });

      message.success('Importation d\'inventaire eSIM réussie!');
    } catch (error: any) {
      console.error('Erreur lors de l\'importation:', error);
      
      setLastImportResult({
        success: false,
        error: error.response?.data?.message || error.message,
        timestamp: new Date()
      });

      message.error('Erreur lors de l\'importation d\'inventaire eSIM');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card>
            <Title level={2}>
              <ImportOutlined style={{ marginRight: 8 }} />
              {t('admin.importEsimInventory.title', 'Importation Inventaire eSIM')}
            </Title>
            
            <Paragraph>
              {t('admin.importEsimInventory.description', 
                'Cette page permet d\'importer les packs depuis un fournisseur tiers via l\'API d\'inventaire eSIM.'
              )}
            </Paragraph>

            <Divider />

            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Alert
                message={t('admin.importEsimInventory.warningTitle', 'Attention')}
                description={t('admin.importEsimInventory.warningDescription', 
                  'Cette opération va synchroniser l\'inventaire avec le fournisseur tiers. Assurez-vous que le système est prêt pour cette importation.'
                )}
                type="warning"
                icon={<WarningOutlined />}
                showIcon
              />

              <Card size="small" title="Options d'importation">
                <Space direction="vertical" size="middle">
                  <div>
                    <Text strong>Force Update:</Text>
                    <div style={{ marginTop: 8 }}>
                      <Switch
                        checked={forceUpdate}
                        onChange={setForceUpdate}
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                      />
                      <Text style={{ marginLeft: 12 }}>
                        {forceUpdate 
                          ? t('admin.importEsimInventory.forceUpdateOn', 'Forcer la mise à jour même si les données sont récentes')
                          : t('admin.importEsimInventory.forceUpdateOff', 'Mise à jour normale (respecter le cache)')
                        }
                      </Text>
                    </div>
                  </div>

                  <Alert
                    message={<InfoCircleOutlined style={{ marginRight: 4 }} />}
                    description={
                      forceUpdate 
                        ? t('admin.importEsimInventory.forceUpdateInfo', 
                            'La mise à jour forcée ignorera le cache et récupérera toutes les données depuis le fournisseur.')
                        : t('admin.importEsimInventory.normalUpdateInfo', 
                            'La mise à jour normale utilisera le cache si les données sont récentes.')
                    }
                    type="info"
                    showIcon={false}
                  />
                </Space>
              </Card>

              <Card size="small" title="Action">
                <Space>
                  <h4>Importation des packs :</h4>
                  <Button
                    type="primary"
                    size="large"
                    icon={<ImportOutlined />}
                    loading={loading}
                    onClick={handleImport}
                  >
                    {loading 
                      ? t('admin.importEsimInventory.importing', 'Importation en cours...') 
                      : t('admin.importEsimInventory.startImport', 'Lancer')
                    }
                  </Button>
                </Space> <br/> <br/>
                <Space>
                   <h4>Importation des eSIM :</h4>
                  <Button
                    type="primary"
                    size="large"
                    icon={<ImportOutlined />}
                    loading={loading}
                    onClick={handleSync}
                    style={{marginLeft:5}}
                  >
                    {loading 
                      ? t('admin.importEsimInventory.importing', 'Importation en cours...') 
                      : t('admin.importEsimInventory.startImport', 'Lancer')
                    }
                  </Button>
                </Space>
              </Card>

              {lastImportResult && (
                <Card 
                  size="small" 
                  title={`Résultat de la dernière importation - ${lastImportResult.timestamp.toLocaleString()}`}
                >
                  {lastImportResult.success ? (
                    <Alert
                      message="Importation réussie"
                      description={
                        <div>
                          <Text>L'importation d'inventaire eSIM s'est déroulée avec succès.</Text>
                          {lastImportResult.data && (
                            <div style={{ marginTop: 8 }}>
                              <Text strong>Détails:</Text>
                              <pre style={{ marginTop: 4, fontSize: '12px' }}>
                                {JSON.stringify(lastImportResult.data, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      }
                      type="success"
                      showIcon
                    />
                  ) : (
                    <Alert
                      message="Erreur lors de l'importation"
                      description={
                        <div>
                          <Text>Une erreur s'est produite lors de l'importation:</Text>
                          <div style={{ marginTop: 8 }}>
                            <Text code>{lastImportResult.error}</Text>
                          </div>
                        </div>
                      }
                      type="error"
                      showIcon
                    />
                  )}
                </Card>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ImportEsimInventory;
